import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { FilePdf, Paperclip, X as XIcon, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CV_MAX_BYTES = 5 * 1024 * 1024; // 5MB

export default function PublicApplyPage() {
  const { linkCode } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState(""); // mostra "Riprovo… (2/3)" durante retry
  const [submitted, setSubmitted] = useState(false);
  const [testLink, setTestLink] = useState(null);
  const [form, setForm] = useState({ first_name:"",last_name:"",email:"",phone:"",birth_date:"",city:"",zip_code:"",is_employed:false,available_from:"",notes:"",gdpr_consent:false });
  const [cvFile, setCvFile] = useState(null);
  const [cvError, setCvError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/apply/${linkCode}`).then(r => setInfo(r.data)).catch(() => setError("Posizione non trovata o chiusa")).finally(() => setLoading(false));
  }, [linkCode]);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const onCvChange = (e) => {
    setCvError("");
    const file = e.target.files?.[0] || null;
    if (!file) { setCvFile(null); return; }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setCvError("Solo file PDF accettati");
      setCvFile(null);
      return;
    }
    if (file.size > CV_MAX_BYTES) {
      setCvError(`File troppo grande (max 5MB). Attuale: ${(file.size / 1024 / 1024).toFixed(1)}MB`);
      setCvFile(null);
      return;
    }
    setCvFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { setError("Devi accettare il consenso GDPR"); return; }
    // B72.1 TASK 2: messaggio CV in italiano (non più HTML5 native "Please select a file.")
    if (!cvFile) {
      setCvError("Il CV è obbligatorio. Allega un file PDF (max 5MB).");
      setError("");
      // Scroll al campo CV per visibilità
      const el = document.querySelector('[data-testid="apply-cv-input"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError("");
    setCvError("");
    setSubmitting(true);
    setSubmitStep("");
    // B71 FIX-1/FIX-2: retry x3 con backoff su network/5xx (network blip Cloudflare 520 ecc.)
    const maxAttempts = 3;
    const backoffMs = [0, 2000, 5000];
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        if (attempt > 1) {
          setSubmitStep(`Problema temporaneo, riprovo… (${attempt}/${maxAttempts})`);
          await new Promise(r => setTimeout(r, backoffMs[attempt - 1]));
        }
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : (v || "")));
        fd.append("cv", cvFile);
        const { data } = await axios.post(`${API}/apply/${linkCode}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        setTestLink(data.test_link);
        setSubmitted(true);
        setSubmitting(false);
        return;
      } catch (err) {
        const status = err?.response?.status;
        const isNetworkError = !err.response;
        const isRetryable = isNetworkError || (status >= 500 && status <= 599);
        const isLast = attempt === maxAttempts;
        if (!isRetryable || isLast) {
          // B72 TASK H: messaggio CV friendly nel funnel pubblico.
          // Su 422 (validazione FastAPI), parsifichiamo la causa e mostriamo copy umano.
          let detail = err?.response?.data?.detail;
          if (status === 422) {
            // FastAPI restituisce un array di errori; cerchiamo CV / fields mancanti
            const errs = Array.isArray(detail) ? detail : [];
            const cvMissing = errs.some(e => {
              const loc = (e?.loc || []).join(".");
              return loc.includes("cv") && (e?.type || "").includes("missing");
            });
            const cvFormat = errs.some(e => (e?.msg || "").toLowerCase().includes("pdf"));
            if (cvMissing) {
              detail = "Il CV è obbligatorio. Allega un file PDF (max 5MB) per inviare la candidatura.";
            } else if (cvFormat) {
              detail = "Il CV deve essere in formato PDF. Riprova con un file .pdf.";
            } else if (typeof detail === "string") {
              // detail già stringa friendly dal backend (es. "CV troppo grande")
            } else {
              detail = "Alcuni campi non sono validi. Controlla email, nome e CV e riprova.";
            }
          } else if (status === 413) {
            detail = "Il CV è troppo grande (max 5MB). Comprimilo e riprova.";
          } else if (status === 415) {
            detail = "Il file caricato non è un PDF valido. Carica il CV in formato PDF.";
          } else if (typeof detail !== "string") {
            detail = isNetworkError ? "Problema temporaneo, riprova tra qualche secondo." : "Errore nell'invio. Riprova tra qualche minuto.";
          }
          setError(detail);
          setSubmitting(false);
          return;
        }
        // continue retry loop
      }
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]"><div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" /></div>;

  // B71 FIX-4: schermata di conferma "Grazie" dopo invio candidatura — utente vede esplicitamente
  // che l'invio è andato a buon fine prima di passare all'analisi (evita doppioni da utente
  // che non sa se è andato a buon fine).
  if (submitted && testLink) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB] p-6" data-testid="apply-thank-you-page">
      <Card className="max-w-md w-full border-[#0D1F3C]/8">
        <CardContent className="p-8 text-center">
          <CheckCircle size={56} weight="duotone" className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#0D1F3C] mb-2" data-testid="apply-thank-you-title">Grazie, candidatura ricevuta!</h2>
          <p className="text-sm text-[#0D1F3C]/70 mb-2">
            Abbiamo registrato la tua candidatura per <strong>{info?.title || "questa posizione"}</strong>.
          </p>
          <p className="text-sm text-[#0D1F3C]/60 mb-6">
            L'ultimo passo è completare l'<strong>analisi comportamentale</strong> (circa 20 minuti).
            Le tue risposte saranno valutate dal team HR.
          </p>
          <Button
            onClick={() => { window.location.href = `/analisi/candidato/${testLink}`; }}
            data-testid="apply-start-test-btn"
            className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C] h-12 text-base"
          >
            Inizia l'analisi
          </Button>
          <p className="text-xs text-[#0D1F3C]/40 mt-4">
            Se devi interrompere, puoi tornare a questo link in seguito.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F7FB] flex items-center justify-center p-6" data-testid="apply-page">
      <Card className="max-w-lg w-full border-[#0D1F3C]/8">
        <CardContent className="p-8">
          <h1 className="text-2xl font-bold text-[#0D1F3C] mb-1">SELLY<span className="text-[#56A3F5]">®</span></h1>
          {info && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-[#0D1F3C] mt-3">{info.title}</h2>
              <p className="text-sm text-[#0D1F3C]/50">{info.company_name || info.sector || ''}</p>
            </div>
          )}
          {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4" data-testid="apply-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Nome *</Label><Input data-testid="apply-first-name" value={form.first_name} onChange={e=>up("first_name",e.target.value)} required /></div>
              <div><Label className="text-xs mb-1 block">Cognome *</Label><Input data-testid="apply-last-name" value={form.last_name} onChange={e=>up("last_name",e.target.value)} required /></div>
            </div>
            <div><Label className="text-xs mb-1 block">Email *</Label><Input data-testid="apply-email" type="email" value={form.email} onChange={e=>up("email",e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Telefono</Label><Input value={form.phone} onChange={e=>up("phone",e.target.value)} /></div>
              <div><Label className="text-xs mb-1 block">Data di nascita</Label><Input type="date" value={form.birth_date} onChange={e=>up("birth_date",e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Comune</Label><Input value={form.city} onChange={e=>up("city",e.target.value)} /></div>
              <div><Label className="text-xs mb-1 block">CAP</Label><Input value={form.zip_code} onChange={e=>up("zip_code",e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.is_employed} onCheckedChange={v=>up("is_employed",v)} /><Label className="text-xs">Attualmente occupato</Label></div>

            {/* BUG-22 fix: CV upload obbligatorio */}
            <div className="border border-dashed border-[#1B4FD8]/30 rounded-lg p-4 bg-[#1B4FD8]/5">
              <Label className="text-xs font-medium text-[#0D1F3C] mb-2 block flex items-center gap-1.5">
                <FilePdf size={16} weight="duotone" className="text-[#1B4FD8]" />
                Allega il tuo CV (PDF, max 5MB) *
              </Label>
              {!cvFile ? (
                <Input type="file" accept="application/pdf,.pdf" onChange={onCvChange}
                  className="text-xs cursor-pointer file:mr-3 file:rounded-md file:border-0 file:bg-[#1B4FD8] file:text-white file:px-3 file:py-1.5 file:text-xs file:cursor-pointer hover:file:bg-[#0D1F3C]"
                  data-testid="apply-cv-input" />
              ) : (
                <div className="flex items-center gap-2 bg-white p-2 rounded-md border border-[#1B4FD8]/20" data-testid="apply-cv-selected">
                  <Paperclip size={16} className="text-[#1B4FD8] flex-shrink-0" />
                  <span className="text-xs text-[#0D1F3C] truncate flex-1">{cvFile.name}</span>
                  <span className="text-[10px] text-[#0D1F3C]/50">{(cvFile.size / 1024 / 1024).toFixed(1)}MB</span>
                  <button type="button" onClick={() => setCvFile(null)} className="text-[#0D1F3C]/40 hover:text-red-500" aria-label="Rimuovi">
                    <XIcon size={14} />
                  </button>
                </div>
              )}
              {cvError && <p className="text-xs text-red-600 mt-1.5" data-testid="apply-cv-error">{cvError}</p>}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox checked={form.gdpr_consent} onCheckedChange={v=>up("gdpr_consent",v)} data-testid="gdpr-consent" />
              <Label className="text-xs text-[#0D1F3C]/60">Acconsento al trattamento dei miei dati personali ai sensi del GDPR *</Label>
            </div>
            <Button type="submit" disabled={submitting} data-testid="apply-submit-btn"
              className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C] h-11 disabled:opacity-60">
              {submitting ? (submitStep || "Invio in corso...") : "Invia Candidatura e Inizia l'analisi"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
